import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { TitleWithHelp, HelpTooltip } from '../components/ui/help-tooltip';
import { Bell, Clock, FileText, Target, ListChecks, RefreshCw, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

export default function ConfigAlertas() {
  const [config, setConfig] = useState({
    oportunidade_parada_ativo: true,
    oportunidade_parada_dias: 7,
    proposta_sem_retorno_ativo: true,
    proposta_sem_retorno_dias: 5,
    tarefa_vencida_ativo: true,
    tarefa_vencida_antecedencia_dias: 1,
    tarefa_proxima_ativo: true,
    tarefa_proxima_dias: 2,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/api/configuracoes/alertas');
      setConfig(c => ({ ...c, ...res.data }));
    } catch {
      // use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/configuracoes/alertas', config);
      toast.success('Configuração salva com sucesso!');
    } catch {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const res = await api.post('/api/configuracoes/alertas/verificar-crm', {});
      const total = res.data.alertas_gerados || 0;
      if (total > 0) {
        toast.success(`${total} novo(s) alerta(s) gerado(s)!`);
      } else {
        toast.info('Nenhum novo alerta encontrado.');
      }
    } catch {
      toast.error('Erro ao verificar alertas');
    } finally {
      setChecking(false);
    }
  };

  const update = (field, value) => setConfig(c => ({ ...c, [field]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const alertTypes = [
    {
      key: 'oportunidade_parada',
      icon: Target,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      title: 'Oportunidades Paradas',
      desc: 'Alerta quando uma oportunidade não recebe interação há X dias.',
      activeField: 'oportunidade_parada_ativo',
      daysField: 'oportunidade_parada_dias',
      daysLabel: 'Dias sem interação',
    },
    {
      key: 'proposta_sem_retorno',
      icon: FileText,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      title: 'Propostas sem Retorno',
      desc: 'Alerta quando uma proposta enviada não recebe resposta há X dias.',
      activeField: 'proposta_sem_retorno_ativo',
      daysField: 'proposta_sem_retorno_dias',
      daysLabel: 'Dias sem retorno',
    },
    {
      key: 'tarefa_vencida',
      icon: Clock,
      color: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
      title: 'Tarefas Vencidas',
      desc: 'Alerta quando uma tarefa passa da data de vencimento sem ser concluída.',
      activeField: 'tarefa_vencida_ativo',
      daysField: 'tarefa_vencida_antecedencia_dias',
      daysLabel: 'Dias de antecedência',
    },
    {
      key: 'tarefa_proxima',
      icon: ListChecks,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      title: 'Tarefas Próximas',
      desc: 'Lembrete quando uma tarefa está próxima do vencimento.',
      activeField: 'tarefa_proxima_ativo',
      daysField: 'tarefa_proxima_dias',
      daysLabel: 'Dias de antecedência',
    },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <TitleWithHelp
            title="Configuração de Alertas"
            description="Configure os alertas automáticos do CRM. O sistema verifica periodicamente e gera notificações no sininho do header."
            connections={['Dashboard', 'CRM', 'Tarefas']}
          >
            <h1 className="text-2xl font-bold text-slate-800">Configuração de Alertas</h1>
          </TitleWithHelp>
          <p className="text-sm text-slate-500 mt-1">Defina quais alertas automáticos devem ser gerados e seus prazos</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" className="gap-1.5"
            onClick={handleCheckNow} disabled={checking}
            data-testid="check-alerts-now-btn"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Verificar Agora
          </Button>
          <Button
            size="sm" className="gap-1.5"
            onClick={handleSave} disabled={saving}
            data-testid="save-alert-config-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {alertTypes.map((a) => (
          <Card key={a.key} className={`${config[a.activeField] ? a.border : 'border-slate-200 opacity-60'} transition-all`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-lg ${a.bg}`}>
                  <a.icon className={`w-5 h-5 ${a.color}`} />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-800 text-sm">{a.title}</h3>
                      <p className="text-xs text-slate-500">{a.desc}</p>
                    </div>
                    <Switch
                      checked={config[a.activeField]}
                      onCheckedChange={(v) => update(a.activeField, v)}
                      data-testid={`toggle-${a.key}`}
                    />
                  </div>
                  {config[a.activeField] && (
                    <div className="flex items-center gap-2 pl-0.5">
                      <Label className="text-xs text-slate-600 whitespace-nowrap">{a.daysLabel}:</Label>
                      <Input
                        type="number" min={1} max={90}
                        value={config[a.daysField]}
                        onChange={(e) => update(a.daysField, parseInt(e.target.value) || 1)}
                        className="h-8 w-20 text-sm text-center"
                        data-testid={`days-${a.key}`}
                      />
                      <span className="text-xs text-slate-400">dias</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-slate-100">
              <Bell className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-slate-700">Verificação Automática</h3>
              <p className="text-xs text-slate-500">O sistema verifica alertas automaticamente a cada 24 horas. Use "Verificar Agora" para disparar uma verificação manual imediata.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
